import { Grid } from '@mui/material';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { NodeRendererProps } from 'react-arborist';
import styled from 'styled-components';

import { useCallback } from 'react';
import { TreeApiData } from '..';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan-details/useRetrieveActivePlanStops';
import { PlanOverviewStopChip } from '../../plan-overview-stop-chip/PlanOverviewStopChip';
import { TREE_ITEM_HEIGHT } from '../PlanOverviewListStyles';

const StopElementContainer = styled(Grid)<{ isLast: boolean }>`
  cursor: pointer;
  height: ${TREE_ITEM_HEIGHT}px;
  padding-right: 6px;
  display: flex;
  align-items: center;
  ${(props) =>
    !props.isLast
      ? ``
      : `
    :after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      height: 1px;
      width: 100%;
      background-color: ${RoutalPalette.neutral10};
    }
    `}
`;

interface StopElementProps extends NodeRendererProps<TreeApiData> {
  selectedStops: string[];
}

export const StopElement = ({ tree, selectedStops, node, style, dragHandle }: StopElementProps) => {
  const { map: stopsMap } = useRetrieveActivePlanStops();
  const { map: driversMap } = useRetrieveActivePlanDrivers();

  const stop = stopsMap[node.data.id];

  const driver = stop.driver_id ? driversMap[stop.driver_id] : undefined;

  if (driver?.is_locked) {
    node.isDraggable = false;
  } else {
    node.isDraggable = true;
  }

  const onClick = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // Only control the Ctrl key or the Command key because the Shift key is used for the library internal multi-selection
    if (e.ctrlKey || e.metaKey) {
      e.stopPropagation();

      let newSelection = [stop.id];
      let anchor: string | null = stop.id;
      let mostRecent: string | null = stop.id;

      const selectedStops = Array.from(tree.selectedIds);

      if (selectedStops.includes(stop.id)) {
        newSelection = selectedStops.filter((stopId) => stopId !== stop.id);
        const lastSelected = newSelection[newSelection.length - 1] ?? null;

        anchor = lastSelected;
        mostRecent = lastSelected;
      } else {
        newSelection = [...selectedStops, stop.id];
      }
      tree.setSelection({ ids: newSelection, anchor, mostRecent });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StopElementContainer
      isLast={(node.parent?.data.id !== `unassigned` && node.data.isLastInLevel) ?? false}
      style={{
        ...style,
      }}
      ref={dragHandle}
    >
      <PlanOverviewStopChip
        driver={driver}
        stop={stop}
        isDragging={node.isDragging}
        selectedStops={selectedStops}
        onClick={onClick}
      />
    </StopElementContainer>
  );
};
